export function openFullResumePdf() {
  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    window.print()
    return
  }

  printWindow.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Jean M. Himebaugh — Resume</title>
  <style>
    @page { size: letter; margin: 0.52in 0.62in; }

    * { box-sizing: border-box; }

    body {
      margin: 0;
      color: #111111;
      font-family: "Times New Roman", Times, serif;
      font-size: 10.5pt;
      line-height: 1.25;
    }

    .resume {
      width: 100%;
      max-width: 7.3in;
      margin: 0 auto;
    }

    header {
      margin-bottom: 14px;
      text-align: center;
    }

    h1 {
      margin: 0;
      font-size: 22pt;
      line-height: 1.05;
      letter-spacing: 0.01em;
    }

    .headline {
      margin: 4px 0 3px;
      font-size: 10.5pt;
      font-weight: bold;
    }

    .contact {
      margin: 0;
      font-size: 9.5pt;
    }

    section {
      margin-top: 13px;
    }

    h2 {
      border-bottom: 1px solid #111111;
      font-size: 10.5pt;
      line-height: 1;
      margin: 0 0 6px;
      padding-bottom: 3px;
      text-transform: uppercase;
    }

    h3, h4, p {
      margin: 0;
    }

    .summary {
      line-height: 1.28;
    }

    .skills {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 3px 16px;
      margin: 0;
      padding-left: 17px;
    }

    .skills li {
      padding-left: 1px;
    }

    .job {
      break-inside: avoid;
      margin: 0 0 13px;
    }

    .job-heading,
    .job-subheading,
    .education-heading {
      display: flex;
      justify-content: space-between;
      gap: 18px;
    }

    .company,
    .school {
      font-size: 11pt;
      font-weight: bold;
    }

    .dates,
    .location {
      font-size: 10pt;
      font-weight: bold;
      text-align: right;
      white-space: nowrap;
    }

    .role {
      font-size: 10.5pt;
      font-style: italic;
      font-weight: bold;
    }

    .domain {
      font-size: 9.5pt;
      font-style: italic;
    }

    ul {
      margin: 3px 0 0;
      padding-left: 18px;
    }

    li {
      margin: 1px 0;
      padding-left: 2px;
    }

    .impact {
      margin-top: 4px;
      font-size: 9.5pt;
      font-weight: bold;
    }

    .tool-group {
      margin: 0 0 3px;
    }

    .tool-group strong {
      display: inline-block;
      min-width: 122px;
    }

    .education-item {
      break-inside: avoid;
      margin-bottom: 8px;
    }

    .credential-detail {
      margin-top: 1px;
    }

    .achievement-list {
      margin-top: 2px;
    }

    @media print {
      body { font-size: 10.5pt; }
    }
  </style>
</head>
<body>
  <main class="resume">
    <header>
      <h1>Jean M. Himebaugh</h1>
      <p class="headline">Sr. UX/UI Designer · NNG Certified User Experience Specialist</p>
      <p class="contact">Los Angeles, CA · (818) 468-2329 · jeanthewebmachine@gmail.com · linkedin.com/in/jeanhimebaugh · jeanthewebmachine.com</p>
    </header>

    <section>
      <h2>Professional Summary</h2>
      <p class="summary">Certified Senior UX/UI Designer with 20+ years of experience leading research-driven design initiatives across enterprise, consumer, government, and healthcare sectors. Proven record of delivering measurable outcomes—from a 75% reduction in system errors for U.S. Space Force platforms to an 80% boost in user engagement for high-net-worth wealth management tools. Deep expertise in the full UX lifecycle: stakeholder research, wireframing, prototyping, design systems, and developer handoff. Bridges design, technology, and business strategy to deliver pixel-perfect, human-centered products at scale.</p>
    </section>

    <section>
      <h2>Core Competencies</h2>
      <ul class="skills">
        <li>UX Research & Strategy</li>
        <li>Design Systems & Style Guides</li>
        <li>Agile / Scrum (Scrum Master)</li>
        <li>User Interviews & Usability Testing</li>
        <li>Wireframing & Prototyping</li>
        <li>Cross-functional Team Leadership</li>
        <li>Interaction Design</li>
        <li>Responsive / Mobile-first Design</li>
        <li>Front-End Development (HTML/CSS/JS)</li>
        <li>UX Audit & Competitive Analysis</li>
        <li>Stakeholder Presentations</li>
        <li>Marketing & Brand Design</li>
      </ul>
    </section>

    <section>
      <h2>Professional Experience</h2>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">Rocket Communications — U.S. Space Force / Government Defense</h3>
          <p class="dates">July 2021 – Present</p>
        </div>
        <p class="role">Staff UX Designer</p>
        <ul>
          <li>Lead UX/UI design for Space Domain Awareness (SDA) and Satellite Operations platforms funded by the U.S. Space Force.</li>
          <li>Designed data-dense mission-critical interfaces using the Astro UX Design System framework, reducing cognitive load and system errors.</li>
          <li>Collaborated with engineers, product managers, and government stakeholders to translate complex requirements into intuitive workflows.</li>
          <li>Produced full UX deliverables: user flows, wireframes, high-fidelity mockups, interactive prototypes, and design specifications via Figma.</li>
          <li>Presented design rationale to senior leadership backed by user research, usability metrics, and qualitative analysis.</li>
          <li>Contributed to Space Symposium 2024 branding and event design collateral.</li>
        </ul>
        <p class="impact">Impact: 75% fewer system errors · 60% faster operator training · 45% boost in team efficiency</p>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">City National Rochdale — Wealth Management / Financial Services</h3>
          <p class="dates">September 2017 – July 2021</p>
        </div>
        <p class="role">Sr. User Experience & Interaction Designer</p>
        <ul>
          <li>Built and led a UX design team from the ground up to serve high-net-worth private wealth clients.</li>
          <li>Redesigned the portfolio proposal platform with enhanced customization, increasing proposal production by 80%.</li>
          <li>Established mobile-first solutions that significantly expanded accessibility and advisor productivity.</li>
          <li>Conducted and managed stakeholder interviews, user research sessions, and usability testing across all product lines.</li>
          <li>Streamlined cross-functional communications from concept through launch in an Agile environment.</li>
          <li>Delivered UX audit reports, task flows, journey maps, component libraries, and developer specifications.</li>
        </ul>
        <p class="impact">Impact: 80% increase in user engagement · 65% higher customer satisfaction · 50% reduction in manual tasks</p>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">Walt Disney Animation Studios — Entertainment / Animation Technology</h3>
          <p class="dates">August 2013 – September 2017</p>
        </div>
        <p class="role">Sr. Software Engineer, Web Technology</p>
        <ul>
          <li>Designed and developed StoryPad—an iPad-native storyboard collaboration tool used by artists, editors, and directors on feature films including Frozen, Tangled, and Wreck-It Ralph.</li>
          <li>Built and maintained image/video clip management, production tracking, personnel assignment, and document management systems.</li>
          <li>Created WordPress plugins and designed UI for internal production networks supporting hundreds of studio artists.</li>
          <li>Served as the design-to-development bridge, ensuring pixel-accurate implementation across all platforms.</li>
        </ul>
        <p class="impact">Impact: 40% gain in storyboard efficiency · 35% faster production timeline · Workflow optimization at scale</p>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">Cedars-Sinai Medical Center — Healthcare</h3>
          <p class="dates">January 2009 – October 2011</p>
        </div>
        <p class="role">Web Designer & Technology Specialist, Enterprise Information Systems</p>
        <ul>
          <li>Designed and deployed campus-wide informational kiosk dashboards featuring real-time news, notifications, and content for hospital guests.</li>
          <li>Served as EIS Web Team liaison for internal staff and external vendors across print and digital channels.</li>
          <li>Delivered UI/UX wireframing, responsive mobile designs, and visual design for multiple corporate and internal websites.</li>
        </ul>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">Disney Interactive Media Group — Gaming / Digital Entertainment</h3>
          <p class="dates">October 2011 – October 2012</p>
        </div>
        <p class="role">Web Designer / Developer</p>
        <ul>
          <li>Front-end visual designer for Disney's Fairies PixieHollow.com online gaming platform serving millions of users.</li>
          <li>Delivered email campaign graphics, responsive/mobile-accessible designs, and dynamic calendar components.</li>
        </ul>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">CityGrid (IAC) — Digital Media</h3>
          <p class="dates">October 2012 – August 2013</p>
        </div>
        <p class="role">Marketing Print/Web Designer</p>
        <ul>
          <li>Design lead for all marketing and sales collateral including print campaigns, web and mobile graphics.</li>
          <li>Managed WordPress development, company branding, and sales team marketing materials.</li>
        </ul>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">MTV Networks — Media & Entertainment</h3>
          <p class="dates">November 2007 – July 2008</p>
        </div>
        <p class="role">Web Designer (Social Projects)</p>
        <ul>
          <li>Created 40+ branded online community environments for major properties including 50 Cent, GoNintendo, Pussycat Dolls, MTV, The Daily Show, Disney Radio, and The Hobbit.</li>
        </ul>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">LoanToolbox.com — Financial Services / E-Learning</h3>
          <p class="dates">September 2002 – November 2007</p>
        </div>
        <p class="role">Web Designer / Producer</p>
        <ul>
          <li>Lead designer for 250+ animated tutorials, seminar speaker decks, email newsletters, and marketing materials.</li>
          <li>Managed end-to-end design production for all digital and print brand assets over a five-year tenure.</li>
        </ul>
      </article>

      <article class="job">
        <div class="job-heading">
          <h3 class="company">Jet Propulsion Laboratory (JPL / NASA) — Government / Research</h3>
          <p class="dates">May 2000 – May 2002</p>
        </div>
        <p class="role">Graphic Designer</p>
        <ul>
          <li>Provided production support and workflow for Documentation Services, interactive web applications, and multimedia design at one of the world's premier scientific research institutions.</li>
        </ul>
      </article>
    </section>

    <section>
      <h2>Tools & Technologies</h2>
      <p class="tool-group"><strong>UX / Design:</strong> Figma · Framer · Sketch · Adobe XD · InVision · Zeplin · Axure RP · Mural · Abstract</p>
      <p class="tool-group"><strong>Adobe Creative Suite:</strong> Illustrator · Photoshop · InDesign · After Effects</p>
      <p class="tool-group"><strong>Front-End:</strong> HTML5 · CSS3 · JavaScript · WordPress · Responsive Web Design</p>
      <p class="tool-group"><strong>Collaboration:</strong> JIRA · Confluence · Slack · Zoom · Microsoft Teams · Google Workspace</p>
      <p class="tool-group"><strong>Platforms:</strong> PC / Mac · iOS · Android · Kiosk / Enterprise</p>
    </section>

    <section>
      <h2>Education & Certifications</h2>

      <div class="education-item">
        <div class="education-heading">
          <p class="school">Nielsen Norman Group (NNG)</p>
          <p class="dates">2020</p>
        </div>
        <p class="credential-detail">UX Certification — User Experience Design & Management · Certification #1034903</p>
      </div>

      <div class="education-item">
        <div class="education-heading">
          <p class="school">Interaction Design Foundation</p>
          <p class="dates">2017</p>
        </div>
        <p class="credential-detail">User Experience & Interaction Design · User Research & User-Centered Design</p>
      </div>

      <div class="education-item">
        <div class="education-heading">
          <p class="school">California State University, Northridge (CSUN)</p>
          <p class="dates">1999–2002</p>
        </div>
        <p class="credential-detail">Bachelor of Science, Computer Science (C++, Java, HTML)</p>
      </div>
    </section>

    <section>
      <h2>Key Achievements</h2>
      <ul class="achievement-list">
        <li>Designed mission-critical government UX for U.S. Space Force satellite operations, achieving a 75% reduction in operator workflow errors.</li>
        <li>Built and led the UX team at City National Rochdale, increasing proposal output by 80% through research-driven redesign.</li>
        <li>Invented StoryPad at Walt Disney Animation, accelerating film production by 35% through workflow optimization.</li>
        <li>NNG Certified UX Professional (Certification #1034903), among fewer than 2% of UX practitioners globally to hold the credential.</li>
        <li>Selected as a design contributor for the Young Storytellers 2016 winning project.</li>
      </ul>
    </section>
  </main>

  <script>
    window.onload = function () {
      window.print();
    };
  </script>
</body>
</html>`)

  printWindow.document.close()
}
